import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle,
  Music,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Send,
  Youtube,
  Globe,
  Users as UsersIcon,
  Plus,
  ArrowLeft,
  Info,
  AlertCircle,
  CheckCircle2,
  Headphones,
  Smartphone,
  Mail
} from 'lucide-react';
import ChannelCard from './ChannelCard';
import ChannelFormDrawer from './ChannelFormDrawer';

const Step2Enhanced = ({ formData, setFormData, onNext, onPrev }) => {
  const [selectedChannel, setSelectedChannel] = useState('whatsapp');
  const [errors, setErrors] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [channelFormData, setChannelFormData] = useState({
    handle: '',
    link: '',
    audienceSize: '',
    location: '',
    purpose: ''
  });
  const [drawerErrors, setDrawerErrors] = useState({});

  // Initialize channels with formData or default values
  const [channels, setChannels] = useState(() => {
    const defaultChannels = {
      whatsapp: [],
      tiktok: [],
      instagram: [],
      facebook: [],
      telegram: [],
      youtube: [],
      website: [],
      podcast: [],
      mobile_app: [],
      newsletter: [],
      offline: []
    };
    
    return {
      ...defaultChannels,
      ...(formData.promotionChannels || {})
    };
  });

  // Sync channels with formData when formData changes
  useEffect(() => {
    if (formData.promotionChannels) {
      const defaultChannels = {
        whatsapp: [],
        tiktok: [],
        instagram: [],
        facebook: [],
        telegram: [],
        youtube: [],
        website: [],
        podcast: [],
        mobile_app: [],
        newsletter: [],
        offline: []
      };
      
      setChannels({
        ...defaultChannels,
        ...formData.promotionChannels
      });
    }
  }, [formData.promotionChannels]);

  const channelTypes = [
    { 
      id: 'whatsapp', 
      label: 'WhatsApp', 
      icon: MessageCircle, 
      color: 'from-green-500 to-green-600',
      placeholder: 'Status / Groups / Communities'
    },
    { 
      id: 'tiktok', 
      label: 'TikTok', 
      icon: Music, 
      color: 'from-black to-gray-800',
      placeholder: '@username'
    },
    { 
      id: 'instagram', 
      label: 'Instagram', 
      icon: InstagramIcon, 
      color: 'from-pink-500 to-purple-600',
      placeholder: '@username'
    },
    { 
      id: 'facebook', 
      label: 'Facebook', 
      icon: FacebookIcon, 
      color: 'from-blue-600 to-blue-700',
      placeholder: 'Page name or link'
    },
    { 
      id: 'telegram', 
      label: 'Telegram', 
      icon: Send, 
      color: 'from-blue-400 to-blue-500',
      placeholder: 'Channel/Group link'
    },
    { 
      id: 'youtube', 
      label: 'YouTube', 
      icon: Youtube, 
      color: 'from-red-600 to-red-700',
      placeholder: 'Channel link'
    },
    { 
      id: 'website', 
      label: 'Blog/Website', 
      icon: Globe, 
      color: 'from-indigo-500 to-indigo-600',
      placeholder: 'Website URL'
    },
    { 
      id: 'podcast', 
      label: 'Podcast', 
      icon: Headphones, 
      color: 'from-purple-500 to-purple-700',
      placeholder: 'Podcast name or RSS feed'
    },
    { 
      id: 'mobile_app', 
      label: 'Mobile App', 
      icon: Smartphone, 
      color: 'from-cyan-500 to-cyan-600',
      placeholder: 'App name or store link'
    },
    { 
      id: 'newsletter', 
      label: 'Newsletters', 
      icon: Mail, 
      color: 'from-blue-500 to-blue-600',
      placeholder: 'Newsletter name or signup link'
    },
    { 
      id: 'offline', 
      label: 'Campus/Offline', 
      icon: UsersIcon, 
      color: 'from-orange-500 to-orange-600',
      placeholder: 'Community name'
    }
  ];

  const channelPurposes = [
    'Electronics & Gadgets',
    'Fashion & Style',
    'Education',
    'Lifestyle',
    'Hospitality',
    'Technology',
    'Food & Beverages',
    'Health & Fitness',
    'Entertainment',
    'Travel & Tourism',
    'Sports',
    'Beauty & Cosmetics',
    'Home & Living',
    'Business & Finance',
    'General/Mixed Content'
  ];

  const audienceLocations = [
    'Ghana',
    'Nigeria',
    'Kenya',
    'South Africa',
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Multiple Countries',
    'Campus/University',
    'Local Community'
  ];

  const getDefaultChannel = () => ({
    handle: '',
    link: '',
    stores: {}, // For mobile_app channel type: { play_store: 'link', apple_store: 'link', huawei: 'link' }
    audienceSize: '',
    location: '',
    purpose: ''
  });

  const openAddChannelDrawer = () => {
    setChannelFormData(getDefaultChannel());
    setEditingIndex(null);
    setDrawerErrors({});
    setIsDrawerOpen(true);
  };

  const openEditChannelDrawer = (index) => {
    const channel = channels[selectedChannel][index];
    setChannelFormData({ ...channel });
    setEditingIndex(index);
    setDrawerErrors({});
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingIndex(null);
    setChannelFormData(getDefaultChannel());
    setDrawerErrors({});
  };

  const deleteChannel = (index) => {
    setChannels(prev => ({
      ...prev,
      [selectedChannel]: prev[selectedChannel].filter((_, i) => i !== index)
    }));
    
    // Clear errors for this channel
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`${selectedChannel}_${index}_`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  const validateChannelLink = (channelType, value) => {
    if (!value) return '';

    const patterns = {
      instagram: /^(@[\w.]+|https?:\/\/(www\.)?instagram\.com\/[\w.]+)/i,
      facebook: /^(https?:\/\/(www\.)?(facebook|fb)\.com\/[\w.-]+|[\w\s.-]+)/i,
      tiktok: /^(@[\w.]+|https?:\/\/(www\.)?tiktok\.com\/@[\w.]+)/i,
      youtube: /^(https?:\/\/(www\.)?(youtube\.com\/(c\/|channel\/|user\/)?|youtu\.be\/)[\w-]+)/i,
      telegram: /^(https?:\/\/(t\.me|telegram\.me)\/[\w]+|@[\w]+)/i,
      whatsapp: /^(\+?\d{10,15}|https?:\/\/(wa\.me|chat\.whatsapp\.com)\/[\w]+)/i,
      website: /^https?:\/\/.+/i,
      podcast: /^([\w\s-]+|https?:\/\/([\w-]+\.)?(spotify|apple|google|anchor|buzzsprout|podbean|stitcher|iheartradio|tunein|castbox|pocketcasts|overcast|castro|downcast|podcastaddict|player\.fm|podchaser|listennotes|podcastindex|rss|feed)\.(com|fm|io|net|org)\/[\w\/-]+)/i,
      mobile_app: /^([\w\s-]+|https?:\/\/(play\.google\.com\/store\/apps\/details|apps\.apple\.com\/app|app\.microsoft\.com\/store\/apps\/details|amazon\.com\/gp\/product|f-droid\.org\/packages)\/[\w\/.-]+)/i,
      newsletter: /^([\w\s-]+|https?:\/\/.+|mailto:[\w.-]+@[\w.-]+\.\w+)/i
    };

    if (patterns[channelType]) {
      return patterns[channelType].test(value) ? '' : `Please enter a valid ${channelType} handle or link`;
    }

    return '';
  };

  const validateDrawerForm = () => {
    const newErrors = {};

    // For mobile_app, validate stores instead of handle/link
    if (selectedChannel === 'mobile_app') {
      const stores = channelFormData.stores || {};
      const selectedStores = Object.keys(stores).filter(key => stores.hasOwnProperty(key));
      
      if (selectedStores.length === 0) {
        newErrors.stores = 'Please select at least one app store platform';
      } else {
        // Validate each selected store has a link
        const storeErrors = {};
        selectedStores.forEach(storeKey => {
          if (!stores[storeKey]?.trim()) {
            storeErrors[storeKey] = `${storeKey === 'play_store' ? 'Play Store' : storeKey === 'apple_store' ? 'App Store' : 'Huawei Store'} link is required`;
          } else {
            // Validate link format
            const linkError = validateChannelLink(selectedChannel, stores[storeKey]);
            if (linkError) {
              storeErrors[storeKey] = linkError;
            }
          }
        });
        
        if (Object.keys(storeErrors).length > 0) {
          newErrors.stores = storeErrors;
        }
      }
    } else {
      // Validate handle/link for non-mobile_app channels
      if (!channelFormData.handle && !channelFormData.link) {
        newErrors.handle = 'Channel handle or link is required';
      } else {
        const linkError = validateChannelLink(selectedChannel, channelFormData.handle || channelFormData.link);
        if (linkError) {
          newErrors.handle = linkError;
        }
      }
    }

    // Validate audience size
    if (!channelFormData.audienceSize?.trim()) {
      newErrors.audienceSize = 'Audience size is required';
    }

    // Validate location
    if (!channelFormData.location?.trim()) {
      newErrors.location = 'Primary location is required';
    }

    // Validate purpose
    if (!channelFormData.purpose?.trim()) {
      newErrors.purpose = 'Channel purpose is required';
    }

    setDrawerErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Normalize phone number for WhatsApp channel
  const normalizePhoneForWhatsApp = (value) => {
    if (!value) return value;
    
    // If it's a URL (wa.me or chat.whatsapp.com), return as-is
    if (value.includes('http') || value.includes('wa.me') || value.includes('chat.whatsapp.com')) {
      return value;
    }
    
    // Remove all non-digits
    let cleanPhone = value.replace(/[^\d]/g, "");
    
    // Handle numbers starting with 0 or 233
    if (cleanPhone.startsWith("233")) {
      // Already has country code, use as-is
      return cleanPhone;
    } else if (cleanPhone.startsWith("0")) {
      // Remove leading 0 and add 233
      return "233" + cleanPhone.substring(1);
    } else if (cleanPhone.length > 0) {
      // Local number without 0, add 233
      return "233" + cleanPhone;
    }
    
    return value;
  };

  const saveChannel = () => {
    if (!validateDrawerForm()) {
      return;
    }

    // Save channel data as-is (preserve user's original input format)
    // Normalization will happen only when submitting to payload
    if (editingIndex !== null) {
      // Update existing channel
      setChannels(prev => ({
        ...prev,
        [selectedChannel]: prev[selectedChannel].map((channel, index) => 
          index === editingIndex ? { ...channelFormData } : channel
        )
      }));
    } else {
      // Add new channel
      setChannels(prev => ({
        ...prev,
        [selectedChannel]: [...prev[selectedChannel], { ...channelFormData }]
      }));
    }

    closeDrawer();
  };

  const validateForm = () => {
    const newErrors = {};
    let hasAtLeastOneChannel = false;

    // Check if at least one channel has data
    Object.keys(channels).forEach(channelType => {
      if (channels[channelType].length > 0) {
        channels[channelType].forEach((channel, index) => {
          // Check if this channel has any filled data
          if (channel.handle || channel.link || channel.audienceSize || channel.location || channel.purpose) {
            hasAtLeastOneChannel = true;

            // For mobile_app, validate stores instead of handle/link
            if (channelType === 'mobile_app') {
              const stores = channel.stores || {};
              const selectedStores = Object.keys(stores).filter(key => stores.hasOwnProperty(key));
              
              if (selectedStores.length === 0) {
                newErrors[`${channelType}_${index}_stores`] = 'Please select at least one app store platform';
              } else {
                // Validate each selected store has a link
                selectedStores.forEach(storeKey => {
                  if (!stores[storeKey]?.trim()) {
                    newErrors[`${channelType}_${index}_stores_${storeKey}`] = `${storeKey === 'play_store' ? 'Play Store' : storeKey === 'apple_store' ? 'App Store' : 'Huawei Store'} link is required`;
                  } else {
                    // Validate link format
                    const linkError = validateChannelLink(channelType, stores[storeKey]);
                    if (linkError) {
                      newErrors[`${channelType}_${index}_stores_${storeKey}`] = linkError;
                    }
                  }
                });
              }
            } else {
              // Validate handle/link for non-mobile_app channels
              if (!channel.handle && !channel.link) {
                newErrors[`${channelType}_${index}_handle`] = 'Channel handle or link is required';
              } else {
                const linkError = validateChannelLink(channelType, channel.handle || channel.link);
                if (linkError) {
                  newErrors[`${channelType}_${index}_handle`] = linkError;
                }
              }
            }

            // Validate audience size
            if (!channel.audienceSize) {
              newErrors[`${channelType}_${index}_audienceSize`] = 'Audience size is required';
            }

            // Validate location
            if (!channel.location) {
              newErrors[`${channelType}_${index}_location`] = 'Primary location is required';
            }

            // Validate purpose
            if (!channel.purpose) {
              newErrors[`${channelType}_${index}_purpose`] = 'Channel purpose is required';
            }
          }
        });
      }
    });

    if (!hasAtLeastOneChannel) {
      newErrors.general = 'Please add at least one promotion channel';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Filter out empty channels and normalize phone numbers before saving
      const filteredChannels = {};
      Object.keys(channels).forEach(channelType => {
        filteredChannels[channelType] = channels[channelType]
          .filter(channel => 
            channel.handle || channel.link || channel.audienceSize || channel.location || channel.purpose
          )
          .map(channel => {
            // Normalize WhatsApp phone numbers to always start with 233
            if (channelType === 'whatsapp' && (channel.handle || channel.link)) {
              const phoneValue = channel.handle || channel.link;
              const normalized = normalizePhoneForWhatsApp(phoneValue);
              return {
                ...channel,
                handle: normalized,
                link: normalized
              };
            }
            return channel;
          });
      });

      setFormData(prev => ({ ...prev, promotionChannels: filteredChannels }));
      onNext();
    }
  };

  const selectedChannelData = channelTypes.find(channel => channel.id === selectedChannel);
  const currentChannels = channels[selectedChannel] || [];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="py-4 mx-auto w-full max-w-6xl"
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          B. PROMOTION CHANNEL DETAILS
          <span className="ml-2 text-red-600">(MANDATORY)</span>
        </h2>
        <p className="text-gray-600">
          Declare all intended promotion channels for platform verification
        </p>
      </div>

      {/* UX Guidelines Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
      >
        <div className="flex items-start space-x-3">
          <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-semibold text-blue-900">
              Channel Verification Guidelines:
            </h3>
            <div className="space-y-2 text-xs text-blue-800">
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Provide accurate and clickable links for all social media channels</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>Ensure handles are spelled correctly for verification purposes</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                <span>All declared channels will be reviewed during the vetting process</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Important Note */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-amber-50 rounded-lg border border-amber-200"
      >
        <div className="flex items-start space-x-2">
          <AlertCircle className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              Important Note
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Follower count alone does not determine approval. We value authentic engagement, 
              content quality, and audience relevance over pure numbers.
            </p>
          </div>
        </div>
      </motion.div>

      {/* General Error */}
      {errors.general && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 mb-4 bg-red-50 rounded-lg border border-red-200"
        >
          <p className="text-sm text-red-600">{errors.general}</p>
        </motion.div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Channel Selection */}
        <div className="lg:col-span-1">
          <h3 className="mb-3 text-sm font-semibold text-gray-800">Select Promotion Channel</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-1">
            {channelTypes.map((channel) => {
              const IconComponent = channel.icon;
              const isSelected = selectedChannel === channel.id;
              const channelCount = channels[channel.id]?.length || 0;
              
              return (
                <motion.button
                  key={channel.id}
                  onClick={() => setSelectedChannel(channel.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full p-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected 
                      ? `border-transparent bg-gradient-to-r ${channel.color} text-white shadow-lg` 
                      : 'border-gray-300 bg-white hover:border-gray-400 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <IconComponent className="w-5 h-5" />
                      <span className="text-sm font-medium">{channel.label}</span>
                    </div>
                    {channelCount > 0 && (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        isSelected ? 'bg-white/20' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {channelCount}
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Right Column - Channel Details */}
        <div className="lg:col-span-2">
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedChannelData?.label}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {selectedChannelData?.placeholder}
                </p>
              </div>
              <motion.button
                onClick={openAddChannelDrawer}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 space-x-2 text-sm text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 hover:from-red-600 hover:to-red-700 shadow-red-500/25"
              >
                <Plus className="w-4 h-4" />
                <span>Add {selectedChannelData?.label}</span>
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {currentChannels.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center text-gray-500"
                >
                  {selectedChannelData && <selectedChannelData.icon className="mx-auto mb-3 w-16 h-16 text-gray-300" />}
                  <p className="text-sm">No {selectedChannelData?.label} channels added yet</p>
                  <p className="mt-1 text-xs text-gray-400">Click "Add {selectedChannelData?.label}" to get started</p>
                </motion.div>
              ) : (
                <motion.div
                  key="channels"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  {currentChannels.map((channel, index) => (
                    <ChannelCard
                      key={index}
                      channel={channel}
                      channelType={selectedChannel}
                      index={index}
                      onEdit={openEditChannelDrawer}
                      onDelete={deleteChannel}
                      icon={selectedChannelData?.icon}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <motion.button
          onClick={onPrev}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </motion.button>

        <motion.button
          onClick={handleSubmit}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-8 py-3 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40"
        >
          Continue
        </motion.button>
      </div>

      {/* Channel Form Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDrawer}
              className="fixed inset-0 z-40 bg-black bg-opacity-50"
            />
            
            {/* Drawer */}
            <ChannelFormDrawer
              isOpen={isDrawerOpen}
              channelData={channelFormData}
              setChannelData={setChannelFormData}
              channelType={selectedChannel}
              channelLabel={selectedChannelData?.label}
              editingIndex={editingIndex}
              onClose={closeDrawer}
              onSave={saveChannel}
              errors={drawerErrors}
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Step2Enhanced;
